import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import type { ParametersNewTemplate } from "../models/ParametersNewTemplate";
import type { Template } from "../models/Template";

const moment = getMomentTz();

export const getShiftParameters = async (AssetId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "AssetId", value: AssetId });

	const resp = await apiService.callV2(
		"[MES].[GetShiftParameters]",
		parameters,
	);
	return resp;
};

export const getTemplates = async (
	AssetId: number | string,
	SearchText: string,
): Promise<{ ok: boolean; data?: Template[]; message?: string | null }> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "AssetId", value: AssetId !== "A" && AssetId });
	parameters.push({ name: "SearchText", value: SearchText });

	const resp = await apiService.callV2("[MMS].[GetTemplates]", parameters);

	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map((row: Template) => {
			return {
				...row,
				ScheduleStart: row.ScheduleStart
					? moment.utc(row.ScheduleStart).toDate()
					: null,
			};
		});
	}

	return resp;
};

export const createPMOrder = async (TemplateId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "TemplateId", value: TemplateId });

	const resp = await apiService.callV2("[MMS].[CreatePMOrder]", parameters);
	return resp;
};

export const deleteTemplate = async (templateId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "TemplateId", value: templateId });

	const resp = await apiService.callV2("[MMS].[DeleteTemplate]", parameters);
	return resp;
};

export const upsertTemplate = async (pNT: ParametersNewTemplate) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "TemplateId", value: pNT.TemplateId });
	parameters.push({ name: "AssetId", value: pNT.AssetId });
	parameters.push({ name: "TypeCode", value: pNT.TypeCode });
	parameters.push({ name: "Title", value: pNT.Title });
	parameters.push({ name: "CraftId", value: pNT.CraftId });
	parameters.push({
		name: "PeriodShifts",
		value: (pNT.selectedPeriodDays ?? 0) * pNT.shiftsPerDay,
	});
	parameters.push({
		name: "WarningShifts",
		value: (pNT.selectedWarningDays ?? 0) * pNT.shiftsPerDay,
	});
	parameters.push({
		name: "ExpectedShifts",
		value: (pNT.selectedExpectedDays ?? 0) * pNT.shiftsPerDay,
	});
	parameters.push({
		name: "NotifyOnGeneration",
		value: !!pNT.NotifyOnGeneration,
	});
	parameters.push({
		name: "DownHours",
		value: Number.parseInt(pNT.DownHours as any),
	});
	parameters.push({
		name: "DowntimeClassCode",
		value: pNT.DowntimeClassCode ? "D" : "N",
	});
	parameters.push({
		name: "PlannedHours",
		value: Number.parseInt(pNT.PlannedHours as any),
	});
	parameters.push({ name: "Description", value: pNT.Description });
	parameters.push({
		name: "NumberOfWorkers",
		value: Number.parseInt(pNT.NumberOfWorkers as any),
	});
	parameters.push({
		name: "SchedulingPolicyCode",
		value: pNT.SchedulingPolicyCode,
	});
	parameters.push({
		name: "ScheduleStart",
		value: moment.utc(pNT.ScheduleStart).toISOString(),
	});
	parameters.push({
		name: "CronExpression",
		value: pNT.CronExpression as string,
	});

	const resp = await apiService.callV2("[MMS].[UpsertTemplate]", parameters);
	return resp;
};

// ATTACHMENTS
export const getTemplateAttachments = async (TemplateId: string | number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TemplateId", value: Number.parseInt(TemplateId as string) },
	];
	return await apiService.callV2("[MMS].[GetTemplateAttachments]", parameters);
};

export const linkTemplateAttachments = async (
	templateId: number,
	attachments: {
		UploadFileId: number;
		FileName: string;
		IsAssetResource: boolean;
	}[],
) => {
	const apiService = new MESApiService();
	const formattedAttachments = attachments
		.map(
			(att) =>
				`[${att.UploadFileId}]:[${att.FileName}]:${
					att.IsAssetResource ? 1 : 0
				}`,
		)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "Attachments", value: formattedAttachments },
	];
	return await apiService.callV2("[MMS].[LinkTemplateAttachments]", parameters);
};

export const unLinkTemplateAttachments = async (
	templateId: number,
	attachments: { UploadFileId: number; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedAttachments = attachments
		.map((att) => `[${att.UploadFileId}]:${att.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "Attachments", value: formattedAttachments },
	];
	return await apiService.callV2(
		"[MMS].[UnlinkTemplateAttachments]",
		parameters,
	);
};

// DRAWINGS
export const getTemplateDrawings = async (TemplateId: number | string) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TemplateId", value: Number.parseInt(TemplateId as string) },
	];
	return await apiService.callV2("[MMS].[GetTemplateDrawings]", parameters);
};

export const linkTemplateDrawings = async (
	templateId: number,
	documentNames: { DocumentName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedDocumentNames = documentNames
		.map((doc) => `[${doc.DocumentName}]:${doc.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "DocumentNames", value: formattedDocumentNames },
	];
	return await apiService.callV2("[MMS].[LinkTemplateDrawings]", parameters);
};

export const unLinkTemplateDrawings = async (
	templateId: number,
	documentNames: { DocumentName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedDocumentNames = documentNames
		.map((doc) => `[${doc.DocumentName}]:${doc.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "DocumentNames", value: formattedDocumentNames },
	];
	return await apiService.callV2("[MMS].[UnlinkTemplateDrawings]", parameters);
};

// LOTO
export const getTemplateLOTO = async (TemplateId: number | string) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TemplateId", value: Number.parseInt(TemplateId as string) },
	];
	return await apiService.callV2("[MMS].[GetTemplateLOTO]", parameters);
};

export const linkTemplateLOTO = async (
	templateId: number,
	LOTOs: { LOTONumber: string; LOTOName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedLOTOs = LOTOs.map(
		(LOTO) =>
			`[${LOTO.LOTONumber}]:[${LOTO.LOTOName}]:${LOTO.IsAssetResource ? 1 : 0}`,
	).join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "LOTOs", value: formattedLOTOs },
	];
	return await apiService.callV2("[MMS].[LinkTemplateLOTO]", parameters);
};

export const unLinkTemplateLOTO = async (
	templateId: number,
	LOTOs: { LOTONumber: string; LOTOName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedLOTOs = LOTOs.map(
		(LOTO) =>
			`[${LOTO.LOTONumber}]:[${LOTO.LOTOName}]:${LOTO.IsAssetResource ? 1 : 0}`,
	).join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "LOTOs", value: formattedLOTOs },
	];
	return await apiService.callV2("[MMS].[UnlinkTemplateLOTO]", parameters);
};

// PARTS
export const getTemplateParts = async (TemplateId: number | string) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TemplateId", value: Number.parseInt(TemplateId as string) },
	];
	return await apiService.callV2("[MMS].[GetTemplateParts]", parameters);
};

export const linkTemplateParts = async (
	templateId: number,
	Parts: { PartName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedParts = Parts.map(
		(part) => `[${part.PartName}]:${part.IsAssetResource ? 1 : 0}`,
	).join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "Parts", value: formattedParts },
	];
	return await apiService.callV2("[MMS].[LinkTemplateParts]", parameters);
};

export const unLinkTemplateParts = async (
	templateId: number,
	parts: { PartName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedParts = parts
		.map((part) => `[${part.PartName}]:${part.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "Parts", value: formattedParts },
	];
	return await apiService.callV2("[MMS].[UnlinkTemplateParts]", parameters);
};

export const getAssetResources = async (
	AssetId: string | number | undefined,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});

	const resp = await apiService.callV2("[MMS].[GetAssetResources]", parameters);
	return resp;
};

export const getTemplateResources = async (TemplateId: number | string) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "TemplateId", value: TemplateId });

	const resp = await apiService.callV2(
		"[MMS].[GetTemplateResources]",
		parameters,
	);
	return resp;
};
